<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Detalle del grupo de cuotas</h1>
    </div>

    <div v-if="movements.length > 0">

      <!-- Header cards -->
      <div class="flex flex-wrap mb-4 -mx-2">
        <div class="w-full md:w-1/2 px-2">
          <wz-card>
            <template #header>
              <div class="wz-card-title">Información del movimiento</div>
            </template>
              <table class="summary-table w-full text-sm px-2">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width:40%">Colaborador</th>
                    <td>{{ header.employeeName }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">DNI/Doc</th>
                    <td>{{ header.employeeIdentifier }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Empresa</th>
                    <td>{{ header.companyName || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Área</th>
                    <td>{{ header.areaName || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Concepto</th>
                    <td>
                      {{ header.conceptName }}
                      <wz-badge class="ml-2" :variant="header.conceptType === 'DESCUENTO' ? 'danger-solid' : 'success-solid'">
                        {{ header.conceptType === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                      </wz-badge>
                    </td>
                  </tr>
                  <tr>
                    <th class="text-muted">Monto total</th>
                    <td>{{ formatAmount(header.totalAmount, header.currency) }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">N° cuotas</th>
                    <td>{{ header.totalInstallments }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Moneda</th>
                    <td>{{ header.currency }}</td>
                  </tr>
                  <tr v-if="firstDescription">
                    <th class="text-muted">Descripción</th>
                    <td>{{ firstDescription }}</td>
                  </tr>
                </tbody>
              </table>
          </wz-card>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <!-- Comprobante -->
          <wz-card class="mb-3">
            <template #header>
              <div class="wz-card-title">Comprobante</div>
            </template>
              <table class="summary-table w-full text-sm px-2">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width:40%">Tipo</th>
                    <td>{{ header.voucherType || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">N° comprobante</th>
                    <td>{{ header.voucherNumber || '-' }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Archivo</th>
                    <td>
                      <a v-if="header.attachmentUrl" href="#" @click.prevent="downloadFile" class="text-primary">
                        <i class="ri-file-line mr-1"></i>{{ header.attachmentName || 'Descargar' }}
                      </a>
                      <span v-else class="text-muted">Sin archivo</span>
                    </td>
                  </tr>
                  <tr>
                    <th class="text-muted">Registrado por</th>
                    <td>{{ header.createdBy }}</td>
                  </tr>
                </tbody>
              </table>
          </wz-card>

          <!-- Resumen -->
          <wz-card>
            <template #header>
              <div class="wz-card-title">Resumen</div>
            </template>
              <table class="summary-table w-full text-sm px-2">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width:40%">Cuotas pagadas</th>
                    <td>{{ paidCount }} de {{ header.totalInstallments }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Monto pagado</th>
                    <td>{{ formatAmount(paidAmount, header.currency) }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Saldo pendiente</th>
                    <td>{{ formatAmount(pendingAmount, header.currency) }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Estado</th>
                    <td>
                      <wz-badge :variant="groupStatusColor">{{ groupStatusLabel }}</wz-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
          </wz-card>
        </div>
      </div>

      <!-- Cronograma de cuotas -->
      <wz-card class="mb-4" :body-padding="false">
        <template #header-title>Cronograma de cuotas</template>
        <template #header-actions>
          <span class="text-muted text-sm">{{ movements.length }} cuota(s)</span>
        </template>
          <table class="wz-table w-full px-2">
            <thead>
              <tr>
                <th>N°</th>
                <th>Período</th>
                <th class="text-right">Monto</th>
                <th>Estado</th>
                <th>Descripción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in movements" :key="m.id">
                <td>{{ m.installmentNumber }}</td>
                <td>{{ getMonthName(m.periodMonth) }} {{ m.periodYear }}</td>
                <td class="text-right">{{ formatAmount(m.amount, m.currency) }}</td>
                <td>
                  <wz-badge :variant="getMovementStatusColor(m.status)">{{ m.status }}</wz-badge>
                </td>
                <td>{{ m.description || '-' }}</td>
              </tr>
            </tbody>
          </table>
      </wz-card>

      <!-- Actions -->
      <div class="flex justify-between">
        <wz-button variant="light" @click="$router.push({ name: 'payroll-movement-list' })">Volver</wz-button>
        <wz-button
          v-if="pendingCount > 0"
          variant="danger"
          @click="openCancelModal"
        >
          <i class="ri-close-circle-line mr-1"></i>Cancelar cuotas pendientes
        </wz-button>
      </div>
    </div>

    <!-- Cancel modal -->
    <wz-modal v-model="cancelModal" title="Cancelar cuotas pendientes">
      <p>
        ¿Está seguro que desea cancelar las cuotas pendientes?
        Se cancelarán <strong>{{ pendingCount }}</strong> cuota(s) por un total de
        <strong>{{ formatAmount(pendingAmount, header ? header.currency : 'PEN') }}</strong>.
      </p>
      <div class="flex justify-end mt-3">
        <wz-button variant="light" class="mr-2" @click="cancelModal = false">Cerrar</wz-button>
        <wz-button variant="danger" @click="doCancelInstallments">Confirmar cancelación</wz-button>
      </div>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { PayrollMovementService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'PayrollMovementGroupDetail',

  data() {
    return {
      movements:   [],
      cancelModal: false,
    }
  },

  computed: {
    header() {
      return this.movements.length > 0 ? this.movements[0] : {}
    },
    firstDescription() {
      if (!this.header.description) return null
      const desc = this.header.description
      const prefix = `Cuota 1/${this.header.totalInstallments} - `
      return desc.startsWith(prefix) ? desc.substring(prefix.length) : desc
    },
    paidCount() {
      return this.movements.filter(m => m.status === 'PROCESADO').length
    },
    pendingCount() {
      return this.movements.filter(m => m.status === 'PENDIENTE').length
    },
    paidAmount() {
      return this.movements
        .filter(m => m.status === 'PROCESADO')
        .reduce((sum, m) => sum + Number(m.amount), 0)
    },
    pendingAmount() {
      return this.movements
        .filter(m => m.status === 'PENDIENTE')
        .reduce((sum, m) => sum + Number(m.amount), 0)
    },
    groupStatus() {
      const total = this.movements.length
      const cancelled = this.movements.filter(m => m.status === 'CANCELADO').length
      if (cancelled === total) return 'CANCELADO'
      if (this.pendingCount === 0) return 'COMPLETADO'
      return 'EN_CURSO'
    },
    groupStatusLabel() {
      const s = LocalConstants.InstallmentGroupStatus[this.groupStatus]
      return s ? s.label : this.groupStatus
    },
    groupStatusColor() {
      const s = LocalConstants.InstallmentGroupStatus[this.groupStatus]
      return s ? s.color : 'dark'
    },
  },

  methods: {
    formatAmount(amount, currency) {
      if (amount == null) return '-'
      const formatted = Number(amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${currency || 'S/'} ${formatted}`
    },
    getMonthName(month) {
      return MONTH_NAMES[(month || 1) - 1]
    },
    getMovementStatusColor(status) {
      const s = LocalConstants.PayrollMovementStatus[status]
      return s ? s.color : 'dark'
    },

    openCancelModal() {
      this.cancelModal = true
    },

    async doCancelInstallments() {
      try {
        await this.$store.dispatch('app/loading', true)
        await PayrollMovementService.cancelFutureInstallments(this.$route.params.groupId)
        this.cancelModal = false
        Alert.success('Cuotas pendientes canceladas correctamente')
        await this.load()
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al cancelar las cuotas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async downloadFile() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollMovementService.downloadAttachment(this.header.id)
        const url = URL.createObjectURL(new Blob([resp.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = this.header.attachmentName || 'adjunto'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el archivo')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async load() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollMovementService.getInstallmentGroup(this.$route.params.groupId)
        this.movements = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el grupo de cuotas')
        this.$router.push({ name: 'payroll-movement-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  created() {
    this.load()
  },
}
</script>

<style lang="stylus" scoped>
.summary-table
  th
    text-align left
    font-weight normal
    padding 4px 0
    vertical-align top
  td
    padding 4px 0
    vertical-align top
</style>
